import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { usePlots, useTasks } from "@/hooks/useFirestore";
import { useOrgPaths } from "@/hooks/useOrgPaths";
import { Experiment } from "@/types/experiment";
import { Plot } from "@/types/plot";
import { Calendar, Copy, Edit, Grid3X3, ListTodo, MapPin, MoreHorizontal, Trash2 } from "lucide-react";
import React from "react";
import { Link } from "react-router-dom";

interface ExperimentCardProps {
  experiment: Experiment;
  trialId: string;
  onEdit?: (experiment: Experiment) => void;
  onDelete?: (experiment: Experiment) => void;
  onDuplicate?: (experiment: Experiment, plots: Plot[]) => void;
  canEdit?: boolean;
  canDelete?: boolean;
}

const ExperimentCard: React.FC<ExperimentCardProps> = ({
  experiment,
  trialId,
  onEdit,
  onDelete,
  onDuplicate,
  canEdit = false,
  canDelete = false,
}) => {
  const { tasks, loading: tasksLoading } = useTasks(trialId, experiment.id);
  const { plots, loading: plotsLoading, error: plotsError } = usePlots(trialId, experiment.id);
  const paths = useOrgPaths();
  const preventCardNavigation = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit?.(experiment);
  };

  const handleDuplicateClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onDuplicate?.(experiment, plots);
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete?.(experiment);
  };

  // Count completed tasks
  const completedTasks = tasks.filter(t => t.state === "completed").length;
  const totalTasks = tasks.length;

  return (
    <>
      <Link
        to={paths.experiment(trialId, experiment.id)}
        className="block"
      >
        <div className="border rounded-lg p-4 hover:shadow-md transition-shadow relative bg-card">
          <div className="flex justify-between items-start">
            <div className="pr-10 flex-1">
              <h3 className="font-medium text-lg text-agri-green-dark">{experiment.name}</h3>
              {experiment.description && (
                <p className="text-muted-foreground text-sm mt-1 line-clamp-2">{experiment.description}</p>
              )}

              <div className="flex flex-wrap gap-4 mt-3">
                {experiment.location && (
                  <div className="flex items-center text-muted-foreground text-sm">
                    <MapPin className="h-4 w-4 mr-1" />
                    <span>{experiment.location}</span>
                  </div>
                )}

                <div className="flex items-center text-muted-foreground text-sm">
                  <Grid3X3 className="h-4 w-4 mr-1" />
                  <span>{plotsLoading ? "..." : `${plots.length} plots`}</span>
                </div>

                <div className="flex items-center text-muted-foreground text-sm">
                  <ListTodo className="h-4 w-4 mr-1" />
                  <span>
                    {tasksLoading ? "..." : totalTasks === 0 ? "No tasks" : `${completedTasks}/${totalTasks} tasks`}
                  </span>
                </div>

                {experiment.createdAt && (
                  <div className="flex items-center text-muted-foreground text-sm">
                    <Calendar className="h-4 w-4 mr-1" />
                    <span>Created {experiment.createdAt.toDate().toLocaleDateString()}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Action buttons */}
            <div
              className="absolute top-4 right-4 flex items-center gap-1"
              onClick={preventCardNavigation}>
              {canEdit && onDuplicate && (
                <button
                  onClick={handleDuplicateClick}
                  disabled={plotsLoading || !!plotsError}
                  className="h-8 w-8 flex items-center justify-center rounded-full text-muted-foreground hover:bg-muted focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                  title={plotsLoading ? "Loading plots..." : plotsError ? "Failed to load plots" : "Duplicate"}
                >
                  <Copy className="h-4 w-4" />
                </button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    onClick={preventCardNavigation}
                    className="h-8 w-8 flex items-center justify-center rounded-full text-muted-foreground hover:bg-muted focus:outline-none"
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-32">
                  {canEdit && (
                    <DropdownMenuItem onClick={handleEditClick} className="cursor-pointer">
                      <Edit className="h-4 w-4 mr-2" />
                      Edit
                    </DropdownMenuItem>
                  )}
                  {canDelete && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={handleDeleteClick}
                        className="text-destructive focus:text-destructive cursor-pointer"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </Link>
    </>
  );
};

export default ExperimentCard;
